import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTable, CellProps, Column, Cell, HeaderGroup } from 'react-table'

import Chip, { ChipProps } from '@mui/material/Chip'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import colors from '@/assets/scss/_themes-vars.module.scss'
import SelectInput from '@/components/ui/inputs/SelectInput'
import { SubscriptionType } from '@/utils/subscription/types'

export type StaffData = {
  id: string
  name: string
  surname: string
  email?: string
  status: 'PDS' | 'MEDECIN'
  subscription: SubscriptionType
}

export type StaffsTableProps = {
  data: StaffData[]
  onSubscriptionChange?: (staff: StaffData, subscription: SubscriptionType) => void
}

const rowHeight = 60

export function StaffsTable(props: StaffsTableProps) {
  const { data, onSubscriptionChange } = props
  const { t } = useTranslation()

  const columns = useMemo(() => {
    return [
      {
        Header: t('page.staffs.staffsTable.header.surname'),
        accessor: 'surname',
        Cell: ({ value }: CellProps<StaffData, string>) => (
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '15px' }}>
            {value ? value.toUpperCase() : ''}
          </Typography>
        ),
      },
      {
        Header: t('page.staffs.staffsTable.header.name'),
        accessor: 'name',
        Cell: ({ value }: CellProps<StaffData, string>) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>
            {value}
          </Typography>
        ),
      },
      // email header
      {
        Header: t('page.staffs.staffsTable.header.email'),
        accessor: 'email',
        Cell: ({ value }: CellProps<StaffData, string>) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>
            {value}
          </Typography>
        ),
      },
      // status header
      {
        Header: t('page.staffs.staffsTable.header.status'),
        accessor: 'status',
        Cell: ({ value: status }: CellProps<StaffData, StaffData['status']>) => {
          if (!status) return null
          let color: ChipProps['color']
          switch (status) {
            case 'PDS':
              color = 'primary'
              break
            case 'MEDECIN':
              color = 'secondary'
              break
            default:
              color = 'info'
              break
          }
          return <Chip color={color} label={status} size="small" variant="filled" />
        },
      },
      {
        Header: t('page.staffs.staffsTable.header.subscription'),
        accessor: 'subscription',
        Cell: ({ row }) => (
          <SelectInput
            name="subscription"
            options={Object.values(SubscriptionType).map((value) => {
              return { value, label: value }
            })}
            value={row.original.subscription}
            onChange={(e) => {
              onSubscriptionChange?.(row.original, e.target.value as SubscriptionType)
            }}
          />
        ),
      },
    ] as Column<StaffData>[]
  }, [t, onSubscriptionChange])

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<StaffData>({
    columns,
    data,
  })

  return (
    <Table stickyHeader>
      <TableHead sx={{ bgcolor: colors.gray100 }}>
        {headerGroups.map((headerGroup: HeaderGroup<StaffData>) => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column: HeaderGroup<StaffData>) => {
              return (
                <TableCell {...column.getHeaderProps()} scope="col">
                  {column.render('Header')}
                </TableCell>
              )
            })}
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps()} sx={{ position: 'relative' }}>
        {rows.map((row) => {
          prepareRow(row)
          return (
            <TableRow
              {...row.getRowProps()}
            // sx={{ cursor: 'pointer' }}
            >
              {row.cells.map((cell: Cell<StaffData>) => (
                <>
                  <TableCell {...cell.getCellProps()} align="left" sx={{ height: rowHeight + 'px', verticalAlign: 'middle' }}>
                    {cell.render('Cell')}
                  </TableCell>
                </>
              ))}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

export default StaffsTable
